export type PlanId = 'free' | 'starter' | 'business' | 'pro' | 'agency' | 'premium' | 'enterprise';

export interface PlanLimits {
  quotaMessagesMonthly: number;
  maxWhatsappAccounts: number;
  maxKnowledgeDocs: number;
  maxCampaignRecipients: number;
  maxTeamMembers: number;
  maxAiExtractWeekly: number;
  maxAudioDurationSeconds: number;
  viralBonusMessages?: number;
}

export interface PlanFeatures {
  allowAudioTranscription: boolean;
  allowBulkCampaigns: boolean;
  allowAdvancedHandoff: boolean;
  allowRemoveBranding: boolean;
  allowApiAccess: boolean;
}

export interface PlanPricing {
  monthlyEur: number;
  annualEur: number;
  monthlyXof: number;
}

export interface PlanDefinition {
  id: PlanId;
  name: string;
  description: string;
  badge?: string | null;
  highlight?: boolean;
  pricing: PlanPricing;
  limits: PlanLimits;
  features: PlanFeatures;
  bulletPoints: string[];
  limitations?: string[];
  isActive?: boolean;
}

// ─── TYPES ADD-ONS DE RÉPONSES & COMPTES WHATSAPP ───

export type ResponseAddonId = 'addon-mini' | 'addon-plus' | 'addon-pro' | 'addon-max';

export interface ResponseAddonDefinition {
  id: ResponseAddonId;
  name: string;
  extraMessages: number;
  priceXof: number;
  priceEur: number;
  savingsLabel?: string;
  isPopular?: boolean;
}

export type WhatsappAddonOption = 1 | 2 | 5 | 10;

export interface WhatsappAddonDefinition {
  id: string;
  accountsCount: WhatsappAddonOption;
  label: string;
  priceMonthlyXof: number;
  priceMonthlyEur: number;
}

/**
 * ════════════════════════════════════════════════════════════════════════════
 * CONFIGURATION UNIFIÉE - IMPORT ET EXTRACTION DE CONNAISSANCES IA (SSOT)
 * ════════════════════════════════════════════════════════════════════════════
 */
export const KNOWLEDGE_IMPORT_CONFIG = {
  MAX_INPUT_TEXT_CHARS: 15000,              // 15 000 caractères max (~15 fiches denses)
  MAX_INPUT_FILE_SIZE_BYTES: 5 * 1024 * 1024, // 5 Mo max par fichier importé
  MAX_INPUT_FILE_SIZE_MB: 5,                // 5 Mo (pour affichage utilisateur)
  MAX_INPUT_PDF_PAGES: 15,                  // Jusqu'à 15 pages pour catalogues et menus PDF
  MAX_BATCH_ITEMS: 15,                      // Plafond technique d'extraction par lot
  MAX_ITEM_CONTENT_CHARS: 600,              // Plafond de caractères par fiche unitaire

  // Règle équitable : si < 50% de la limite max, ne consomme que 0.5 crédit (au lieu de 1.0)
  HALF_CONSUMPTION_THRESHOLD_CHARS: 7500,        // < 7 500 car. = 0.5 crédit
  HALF_CONSUMPTION_THRESHOLD_BYTES: 2.5 * 1024 * 1024, // < 2.5 Mo = 0.5 crédit

  WEEKLY_AI_EXTRACT_LIMITS: {
    free: 15,       // 15 crédits / semaine (jusqu'à 30 petites extractions)
    starter: 25,    // 25 crédits / semaine (jusqu'à 50 petites extractions)
    pro: 60,        // 60 crédits / semaine (jusqu'à 120 petites extractions)
    business: 150,  // 150 crédits / semaine
    agency: 350,    // 350 crédits / semaine
    premium: 350,
    enterprise: 350
  } as Record<PlanId, number>
} as const;

/**
 * ════════════════════════════════════════════════════════════════════════════
 * CONFIGURATION CLIENT DES FORFAITS (SYNCHRONISÉE AVEC LE BACKEND SSOT)
 * ════════════════════════════════════════════════════════════════════════════
 */

const freePlan: PlanDefinition = {
  id: 'free',
  name: 'Free',
  description: 'Idéal pour tester l’agent IA et automatiser vos premiers échanges WhatsApp.',
  badge: null,
  highlight: false,
  isActive: true,
  pricing: {
    monthlyEur: 0,
    annualEur: 0,
    monthlyXof: 0
  },
  limits: {
    quotaMessagesMonthly: 50,
    maxWhatsappAccounts: 1,
    maxKnowledgeDocs: 10,
    maxCampaignRecipients: 0,
    maxTeamMembers: 1,
    maxAiExtractWeekly: KNOWLEDGE_IMPORT_CONFIG.WEEKLY_AI_EXTRACT_LIMITS.free,
    maxAudioDurationSeconds: 0,
    viralBonusMessages: 25
  },
  features: {
    allowAudioTranscription: false,
    allowBulkCampaigns: false,
    allowAdvancedHandoff: true,
    allowRemoveBranding: false,
    allowApiAccess: false
  },
  bulletPoints: [
    '1 numéro WhatsApp connecté',
    '1 accès utilisateur (propriétaire)',
    '50 réponses envoyées / mois (renouvelées chaque mois)',
    'Réponses instantanées 24h/7j',
    'Passage de relais humain & alertes temps réel',
    'Mémoire IA : 10 fiches catalogue',
    'Maintien actif : déconnexion automatique si aucun message entrant dans 72h',
    'Support standard par WhatsApp & Email'
  ],
  limitations: [
    'Comptes WhatsApp supplémentaires non disponibles',
    'Gestion d’équipe multi-collaborateurs non incluse',
    'Pas de transcription vocale',
    'Pas de campagnes groupées',
    'Déconnexion automatique après 72h sans message entrant'
  ]
};

const starterPlan: PlanDefinition = {
  id: 'starter',
  name: 'Starter',
  description: 'Idéal pour tester l’agent IA et automatiser ses premiers clients WhatsApp.',
  badge: null,
  highlight: false,
  isActive: true,
  pricing: {
    monthlyEur: 8,
    annualEur: 6,
    monthlyXof: 4900
  },
  limits: {
    quotaMessagesMonthly: 500,
    maxWhatsappAccounts: 1,
    maxKnowledgeDocs: 25,
    maxCampaignRecipients: 0,
    maxTeamMembers: 1,
    maxAiExtractWeekly: KNOWLEDGE_IMPORT_CONFIG.WEEKLY_AI_EXTRACT_LIMITS.starter,
    maxAudioDurationSeconds: 0,
    viralBonusMessages: 100
  },
  features: {
    allowAudioTranscription: false,
    allowBulkCampaigns: false,
    allowAdvancedHandoff: true,
    allowRemoveBranding: false,
    allowApiAccess: false
  },
  bulletPoints: [
    '1 numéro WhatsApp connecté',
    '1 accès utilisateur (propriétaire)',
    '500 réponses envoyées / mois',
    'Réponses instantanées 24h/7j',
    'Passage de relais humain & alertes temps réel',
    'Mémoire IA : 25 fiches catalogue',
    'Support standard par WhatsApp & Email'
  ],
  limitations: [
    'Comptes WhatsApp supplémentaires non disponibles',
    'Gestion d’équipe multi-collaborateurs non incluse',
    'Pas de transcription vocale',
    'Pas de campagnes groupées'
  ]
};

const proPlan: PlanDefinition = {
  id: 'pro',
  name: 'Pro',
  description: 'Pour les commerces et indépendants ayant une activité de vente quotidienne.',
  badge: 'Recommandé',
  highlight: true,
  isActive: true,
  pricing: {
    monthlyEur: 19,
    annualEur: 15,
    monthlyXof: 11900
  },
  limits: {
    quotaMessagesMonthly: 1000,
    maxWhatsappAccounts: 1,
    maxKnowledgeDocs: 75,
    maxCampaignRecipients: 200,
    maxTeamMembers: 2,
    maxAiExtractWeekly: KNOWLEDGE_IMPORT_CONFIG.WEEKLY_AI_EXTRACT_LIMITS.pro,
    maxAudioDurationSeconds: 30,
    viralBonusMessages: 250
  },
  features: {
    allowAudioTranscription: true,
    allowBulkCampaigns: true,
    allowAdvancedHandoff: true,
    allowRemoveBranding: false,
    allowApiAccess: false
  },
  bulletPoints: [
    '1 numéro WhatsApp connecté',
    'Jusqu’à 2 accès collaborateurs',
    '1 000 réponses envoyées / mois',
    'Écoute des notes vocales jusqu’à 30s (langues internationales)',
    'Campagnes groupées (jusqu’à 200 contacts)',
    'Mémoire IA : 75 fiches catalogue',
    'Passage de relais humain & alertes temps réel',
    'Support prioritaire'
  ],
  limitations: [
    'Comptes WhatsApp supplémentaires non disponibles'
  ]
};

const businessPlan: PlanDefinition = {
  id: 'business',
  name: 'Business',
  description: 'Pour les boutiques actives et équipes gérant plusieurs numéros WhatsApp.',
  badge: 'Multi-lignes & Équipe',
  highlight: false,
  isActive: true,
  pricing: {
    monthlyEur: 39,
    annualEur: 32,
    monthlyXof: 24900
  },
  limits: {
    quotaMessagesMonthly: 2500,
    maxWhatsappAccounts: 2,
    maxKnowledgeDocs: 250,
    maxCampaignRecipients: 1000,
    maxTeamMembers: 5,
    maxAiExtractWeekly: KNOWLEDGE_IMPORT_CONFIG.WEEKLY_AI_EXTRACT_LIMITS.business,
    maxAudioDurationSeconds: 60,
    viralBonusMessages: 500
  },
  features: {
    allowAudioTranscription: true,
    allowBulkCampaigns: true,
    allowAdvancedHandoff: true,
    allowRemoveBranding: true,
    allowApiAccess: false
  },
  bulletPoints: [
    '2 numéros WhatsApp connectés inclus',
    'Possibilité d’ajouter des comptes WhatsApp supplémentaires',
    'Comptes supplémentaires conservés à chaque renouvellement (7j de grâce)',
    'Jusqu’à 5 accès collaborateurs / équipe',
    '2 500 réponses envoyées / mois (mutualisées)',
    'Écoute rapide des notes vocales jusqu’à 1 min (langues internationales)',
    'Campagnes groupées (jusqu’à 1 000 contacts)',
    'Mémoire IA : 250 fiches catalogue',
    'Support VIP WhatsApp & Email'
  ],
  limitations: []
};

const agencyPlan: PlanDefinition = {
  id: 'agency',
  name: 'Agence',
  description: 'Pour les agences, franchises et réseaux multi-boutiques à fort volume.',
  badge: 'Puissance maximale',
  highlight: false,
  isActive: true,
  pricing: {
    monthlyEur: 90,
    annualEur: 75,
    monthlyXof: 59000
  },
  limits: {
    quotaMessagesMonthly: 7000,
    maxWhatsappAccounts: 3,
    maxKnowledgeDocs: 1000,
    maxCampaignRecipients: 10000,
    maxTeamMembers: 999,
    maxAiExtractWeekly: KNOWLEDGE_IMPORT_CONFIG.WEEKLY_AI_EXTRACT_LIMITS.agency,
    maxAudioDurationSeconds: 60,
    viralBonusMessages: 1500
  },
  features: {
    allowAudioTranscription: true,
    allowBulkCampaigns: true,
    allowAdvancedHandoff: true,
    allowRemoveBranding: true,
    allowApiAccess: true
  },
  bulletPoints: [
    '3 numéros WhatsApp connectés inclus',
    'Possibilité d’ajouter des comptes WhatsApp supplémentaires',
    'Comptes supplémentaires conservés à chaque renouvellement (7j de grâce)',
    'Membres d’équipe illimités & rôles avancés',
    '7 000 réponses envoyées / mois (mutualisées)',
    'Écoute rapide des notes vocales jusqu’à 1 min (langues internationales)',
    'Campagnes de masse (jusqu’à 10 000 contacts)',
    'Mémoire IA étendue : 1 000 fiches catalogue',
    'Webhooks REST & synchronisation CRM',
    'Gestionnaire de compte dédié & support prioritaire'
  ],
  limitations: []
};

export const PLANS_CONFIG: Record<PlanId, PlanDefinition> = {
  free: freePlan,
  starter: starterPlan,
  pro: proPlan,
  business: businessPlan,
  agency: agencyPlan,
  premium: { ...agencyPlan, id: 'premium' },
  enterprise: { ...agencyPlan, id: 'enterprise' }
};

// ─── 2. ADD-ONS DE RÉPONSES IA (DISPONIBLES SUR TOUS LES PLANS) ───

export const RESPONSE_ADDONS_CONFIG: Record<ResponseAddonId, ResponseAddonDefinition> = {
  'addon-mini': {
    id: 'addon-mini',
    name: 'Mini',
    extraMessages: 250,
    priceXof: 1900,
    priceEur: 3
  },
  'addon-plus': {
    id: 'addon-plus',
    name: 'Plus',
    extraMessages: 500,
    priceXof: 3500,
    priceEur: 5.5,
    savingsLabel: 'Économisez 300 F'
  },
  'addon-pro': {
    id: 'addon-pro',
    name: 'Pro',
    extraMessages: 1000,
    priceXof: 6900,
    priceEur: 11,
    savingsLabel: 'Économisez 700 F',
    isPopular: true
  },
  'addon-max': {
    id: 'addon-max',
    name: 'Max',
    extraMessages: 2500,
    priceXof: 14900,
    priceEur: 23,
    savingsLabel: 'Économisez 4 100 F'
  }
};

// ─── 3. COMPTES WHATSAPP SUPPLÉMENTAIRES (PRO & AGENCE UNIQUEMENT) ───

export const WHATSAPP_ADDONS_CONFIG: Record<string, WhatsappAddonDefinition> = {
  'wa-extra-1': {
    id: 'wa-extra-1',
    accountsCount: 1,
    label: '+1 compte',
    priceMonthlyXof: 2500,
    priceMonthlyEur: 4
  },
  'wa-extra-2': {
    id: 'wa-extra-2',
    accountsCount: 2,
    label: '+2 comptes',
    priceMonthlyXof: 4500,
    priceMonthlyEur: 7
  },
  'wa-extra-5': {
    id: 'wa-extra-5',
    accountsCount: 5,
    label: '+5 comptes',
    priceMonthlyXof: 9900,
    priceMonthlyEur: 15
  },
  'wa-extra-10': {
    id: 'wa-extra-10',
    accountsCount: 10,
    label: '+10 comptes',
    priceMonthlyXof: 18500,
    priceMonthlyEur: 28
  }
};

export function getPlanDefinition(planId?: string | null): PlanDefinition {
  if (!planId) return PLANS_CONFIG.free;
  const normalized = planId.toLowerCase() as PlanId;
  return PLANS_CONFIG[normalized] || PLANS_CONFIG.free;
}

export function getAllActivePlans(): PlanDefinition[] {
  return [PLANS_CONFIG.free, PLANS_CONFIG.starter, PLANS_CONFIG.pro, PLANS_CONFIG.business, PLANS_CONFIG.agency];
}

export function canAddExtraWhatsappAccounts(planId?: string | null): boolean {
  if (!planId) return false;
  const normalized = planId.toLowerCase();
  return (
    normalized === 'business' ||
    normalized === 'agency' ||
    normalized === 'premium' ||
    normalized === 'enterprise'
  );
}

export function getAllResponseAddons(): ResponseAddonDefinition[] {
  return Object.values(RESPONSE_ADDONS_CONFIG);
}

export function getAllWhatsappAddons(): WhatsappAddonDefinition[] {
  return Object.values(WHATSAPP_ADDONS_CONFIG);
}

/**
 * ════════════════════════════════════════════════════════════════════════════
 * MESSAGES UNIFIÉS DE QUOTA & FORFAIT (SSOT)
 * ════════════════════════════════════════════════════════════════════════════
 * Source unique pour tous les messages relatifs aux quotas.
 * Sans jargon technique, sans termes robotiques ('bot' banni), concis et percutant.
 */
export const QUOTA_MESSAGES = {
  // Message principal lorsque le quota Free est épuisé
  FREE_EXHAUSTED_DISCONNECTED:
    "Votre forfait gratuit est épuisé. Passez à un forfait supérieur pour réactiver l'IA.",

  // Version courte pour sous-titres et badges
  FREE_EXHAUSTED_SHORT:
    "Forfait gratuit épuisé.",

  // Message pour forfait payant épuisé
  PAID_EXHAUSTED:
    "Votre forfait de réponses est épuisé. Passez à un forfait supérieur pour réactiver l'IA.",

  // Blocage de création ou d'activation de compte WhatsApp
  FREE_ADD_ACCOUNT_BLOCKED:
    "Votre forfait gratuit est épuisé. Passez à un forfait supérieur pour ajouter ou activer un compte WhatsApp.",

  // Statut visuel de l'IA
  AI_PAUSED_QUOTA:
    "IA en pause (Quota)",

  // Infobulle switch IA bloqué
  AI_PAUSED_QUOTA_TOOLTIP:
    "Quota gratuit épuisé. Passez à un forfait supérieur pour réactiver l'IA.",

  // Infobulle reconnexion directe bloquée
  RECONNECT_PRESERVED_TOOLTIP:
    "Session en veille (quota mensuel atteint). Vos identifiants WhatsApp sont conservés : passez au forfait supérieur pour réactiver la session."
} as const;

/**
 * Renvoie le message d'épuisement de quota approprié selon le plan.
 */
export function getQuotaExceededMessage(planId?: string | null, isFree?: boolean): string {
  const isFreePlan = isFree || planId === 'free';
  return isFreePlan ? QUOTA_MESSAGES.FREE_EXHAUSTED_DISCONNECTED : QUOTA_MESSAGES.PAID_EXHAUSTED;
}

/**
 * Renvoie le nombre de messages bonus accordés lors de l'activation
 * de la Fierté de Marque (Boucle Virale) selon le forfait.
 */
export function getViralBonusMessages(planId?: string | null): number {
  const norm = (planId?.toLowerCase() as PlanId) || 'free';
  return PLANS_CONFIG[norm]?.limits?.viralBonusMessages ?? 50;
}


